<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { ScheduleCardItem } from './ScheduleItemCard.vue'
import { ScheduleItem, fromScheduleItem } from '@/design-system/components'

export interface WeekDay {
  date: Date
  dayName: string
  dayNumber: number
  dateString: string
  isToday: boolean
  isPast: boolean
}

defineProps<{
  days: WeekDay[]
  itemsByDate: Map<string, ScheduleCardItem[]>
  maxItems?: number
  showAddButton?: boolean
  hideClassName?: boolean
}>()

const emit = defineEmits<{
  (e: 'item-click', item: ScheduleCardItem): void
  (e: 'add', date: Date): void
  (e: 'more', dateString: string): void
}>()

const displayFor = (item: ScheduleCardItem) => fromScheduleItem(item)
</script>

<template>
  <div class="week-strip">
    <div
      class="week-grid"
      :style="{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }"
    >
      <div
        v-for="day in days"
        :key="day.dateString"
        class="day-column"
        :class="{
          'day-past': day.isPast && !day.isToday,
          'day-today': day.isToday,
        }"
      >
        <!-- Day header -->
        <div class="day-header">
          <span class="day-name">{{ day.dayName }}</span>
          <span class="day-number tnum">{{ day.dayNumber }}</span>
          <button
            v-if="showAddButton && !day.isPast"
            class="day-add"
            @click.stop="emit('add', day.date)"
            title="Add"
          >
            <font-awesome-icon :icon="['fas', 'plus']" class="text-[10px]" />
          </button>
        </div>

        <!-- Day items -->
        <div class="day-items">
          <template
            v-for="item in (itemsByDate.get(day.dateString) ?? []).slice(0, maxItems ?? 4)"
            :key="item.id"
          >
            <ScheduleItem
              v-if="displayFor(item)"
              :display="displayFor(item)!"
              variant="compact"
              @click="emit('item-click', item)"
            />
          </template>

          <!-- More indicator -->
          <button
            v-if="(itemsByDate.get(day.dateString) ?? []).length > (maxItems ?? 4)"
            class="more-link"
            @click="emit('more', day.dateString)"
          >
            +{{ (itemsByDate.get(day.dateString) ?? []).length - (maxItems ?? 4) }} more
          </button>

          <!-- Empty day -->
          <div
            v-if="(itemsByDate.get(day.dateString) ?? []).length === 0"
            class="day-empty"
          >
            <button
              v-if="showAddButton && !day.isPast"
              class="empty-add"
              @click="emit('add', day.date)"
            >
              <font-awesome-icon :icon="['fas', 'plus']" class="text-xs" />
            </button>
            <div v-else class="empty-glyph">
              <font-awesome-icon :icon="['fas', day.isPast ? 'check' : 'calendar-day']" class="text-xs" />
            </div>
            <p class="empty-label">
              {{ day.isPast ? 'Clear day' : 'Free day' }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.week-strip {
  overflow: hidden;
}

.week-grid {
  display: grid;
}

.day-column {
  min-height: 160px;
  border-right: 1px solid var(--hairline);
  transition: background-color var(--motion-fast);
}

.day-column:last-child {
  border-right: 0;
}

.day-past {
  background: rgba(244, 244, 246, 0.4);
}

.day-header {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: 14px 12px 8px;
}

.day-name {
  font-family: var(--font-sans);
  font-size: var(--text-micro);
  line-height: var(--text-micro-lh);
  letter-spacing: var(--tracking-wide);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  color: var(--ink-400);
}

.day-today .day-name {
  color: var(--ink-700);
}

.day-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: var(--text-body-sm);
  font-weight: var(--weight-bold);
  color: var(--ink-700);
}

.day-today .day-number {
  background: var(--ink-black);
  color: var(--ink-white);
}

.day-add {
  margin-left: auto;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  border-radius: var(--radius-sm);
  color: var(--ink-300);
  cursor: pointer;
  transition: background-color var(--motion-fast), color var(--motion-fast);
}

.day-add:hover {
  background: var(--surface-muted);
  color: var(--ink-700);
}

.day-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 8px 12px;
}

.more-link {
  width: 100%;
  text-align: center;
  font-size: var(--text-micro);
  font-weight: var(--weight-semibold);
  color: var(--ink-700);
  padding: 4px 0;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.more-link:hover {
  color: var(--ink-black);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.day-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-md) 0;
}

.empty-add,
.empty-glyph {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed var(--ink-200);
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--ink-300);
  margin-bottom: 6px;
  cursor: default;
}

.empty-add {
  cursor: pointer;
  transition: border-color var(--motion-fast), color var(--motion-fast);
}

.empty-add:hover {
  border-color: var(--ink-400);
  color: var(--ink-700);
}

.empty-label {
  font-family: var(--font-sans);
  font-size: var(--text-micro);
  font-weight: var(--weight-medium);
  color: var(--ink-400);
  margin: 0;
}
</style>
